import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
    BlockStack,
    InlineStack,
    Text,
    Badge,
    Modal,
    TextField,
    Banner
} from '@shopify/polaris';
import axios from 'axios';
import { resolveUserLabel } from '../utils/userDisplayName';
import {
    PO_WEIGHT_MISSING_CODE,
    formatWeightIssueLine
} from '../utils/productWeight';

const API_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 

    ? 'http://localhost:5100' 
    : (process.env.REACT_APP_API_URL || '');

// One in-flight /my-permissions request shared by every checklist instance
// on the page (a page of PO cards used to fire one per card). Result cached
// 30s; the task-permissions-updated event bypasses the cache via force.
let sharedPermissionsPromise = null;
let sharedPermissionsAt = 0;
function fetchSharedPermissions(force = false) {
    const fresh = (Date.now() - sharedPermissionsAt) < 30000;
    if (!force && sharedPermissionsPromise && fresh) return sharedPermissionsPromise;
    sharedPermissionsAt = Date.now();
    sharedPermissionsPromise = axios.get(`${API_URL}/api/settings/my-permissions`, { withCredentials: true })
        .catch((err) => { sharedPermissionsPromise = null; throw err; });
    return sharedPermissionsPromise;
}

// Default arrived workflow task definitions
const DEFAULT_TASK_DEFINITIONS = [
    {
        key: 'printWorksheet',
        label: '1. Print Worksheet',
        shortLabel: '1. Print Worksheet',
        description: 'Print the receiving worksheet for this PO.',
        recommendedBefore: [],
        actionType: 'printWorksheet',
        actionLabel: 'Print Worksheet'
    },
    {
        key: 'printLabels',
        label: '2. Print Labels',
        shortLabel: '2. Print Labels',
        description: 'Print product labels for this PO.',
        recommendedBefore: ['printWorksheet'],
        actionType: 'printLabels',
        actionLabel: 'Print Labels'
    },
    { 
        key: 'verifyProductData', 
        label: '3. Check weights & barcodes',
        shortLabel: '3. Check weights & barcodes',
        description: 'Verify each item has a valid EAN barcode and correct weight recorded in the system using the grid view.',
        recommendedBefore: ['printWorksheet', 'printLabels']
    },
    { 
        key: 'notifyArrival', 
        label: '4. Notify purchasing what\'s missing/arrived',
        shortLabel: '4. Notify purchasing what\'s missing/arrived',
        description: 'Select items that have arrived and notify purchasing team',
        recommendedBefore: ['printWorksheet', 'printLabels', 'verifyProductData'],
        opensReceiptModal: true // Special handling - opens receipt modal
    },
    {
        key: 'awaitingStockin',
        label: '5. Stock received and approved for putaway',
        shortLabel: '5. Stock received and approved for putaway',
        description: 'Please wait till this is ticked off by purchasing team before allocating warehouse locations and putting on the shelf.',
        recommendedBefore: ['printWorksheet', 'printLabels', 'verifyProductData', 'notifyArrival']
    },
    {
        key: 'checkPreorders',
        label: '6. Check for preorders',
        shortLabel: '6. Check for preorders',
        description: 'Orders get picked as soon as stock is received — ensure all preorders for this PO have been allocated to customer orders before putting stock on shelves.',
        recommendedBefore: ['printWorksheet', 'printLabels', 'verifyProductData', 'notifyArrival', 'awaitingStockin']
    },
    {
        key: 'putAway',
        label: '7. Put goods away on shelves',
        shortLabel: '7. Put goods away on shelves',
        description: 'Goods are now ready to be put away.',
        recommendedBefore: ['printWorksheet', 'printLabels', 'verifyProductData', 'notifyArrival', 'awaitingStockin', 'checkPreorders'],
        requiresAllPrevious: true // Hard requirement - all previous must be done
    }
];

/**
 * Task Checklist component for flexible PO workflow
 * Allows completing tasks in any order with soft warnings
 */
const POTaskChecklist = ({ 
    po, 
    tasks = {}, 
    onTaskUpdate, 
    onTaskAction,
    onOpenReceiptModal, // Callback to open receipt modal for notifyArrival task
    currentUser,
    compact = false,
    darkMode = false,
    taskDefinitions = DEFAULT_TASK_DEFINITIONS,
    taskDefinitionOverrides = null,
    workflowTitle = 'Receiving Workflow',
    fullModeTitle = 'Receiving Checklist',
    completionMessage = 'All tasks complete! This PO is ready to archive.'
}) => {
    const [isLoading, setIsLoading] = useState({});
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [showWeightMissingModal, setShowWeightMissingModal] = useState(false);
    const [weightValidationIssues, setWeightValidationIssues] = useState([]);
    // Step 6 dispatch cross-check result: { mode: 'blocked'|'confirm', blockers, waiting, unknown }
    const [dispatchCheck, setDispatchCheck] = useState(null);
    const [pendingTask, setPendingTask] = useState(null);
    const [overrideReason, setOverrideReason] = useState('');
    const [error, setError] = useState(null);
    const [taskPermissions, setTaskPermissions] = useState(null);
    const activeTaskDefinitions = useMemo(() => {
        const baseDefinitions = Array.isArray(taskDefinitions) && taskDefinitions.length > 0
            ? taskDefinitions
            : DEFAULT_TASK_DEFINITIONS;
        const overrides = taskDefinitionOverrides || {};
        return baseDefinitions.map(taskDef => ({
            ...taskDef,
            ...(overrides[taskDef.key] || {})
        }));
    }, [taskDefinitions, taskDefinitionOverrides]);

    const fetchPermissions = useCallback(async () => {
        try {
            // Shared across every checklist on the page: each PO card mounts
            // one of these, and per-instance fetches hit /my-permissions a
            // dozen times per Incoming Stock load (perf review, 2026-08-29).
            const response = await fetchSharedPermissions();
            setTaskPermissions(response.data.permissions);
        } catch (err) {
            console.error('Error loading task permissions:', err);
            // Use defaults on error
            setTaskPermissions({
                taskPermissions: {
                    printWorksheet: true,
                    printLabels: true,
                    verifyProductData: true,
                    notifyArrival: true,
                    checkPreorders: true,
                    awaitingStockin: false,
                    putAway: true,
                    checkCostingsRetailPricing: true
                }
            });
        }
    }, []);

    // Fetch task permissions on mount and when settings are saved
    useEffect(() => {
        fetchPermissions();

        const handlePermissionsUpdated = () => {
            fetchSharedPermissions(true).then((response) => setTaskPermissions(response.data.permissions)).catch(() => {});
        };

        window.addEventListener('task-permissions-updated', handlePermissionsUpdated);
        return () => {
            window.removeEventListener('task-permissions-updated', handlePermissionsUpdated);
        };
    }, [fetchPermissions]);

    const canSkipWorkflowWarnings = useCallback(() => {
        const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isDev) return true;

        if (!taskPermissions) return false;

        return taskPermissions?.taskPermissions?.skipWorkflowWarnings === true ||
            taskPermissions?.isAdmin === true;
    }, [taskPermissions]);

    const getCompletedByLabel = useCallback((task) => {
        if (!task?.completedBy) return null;
        return resolveUserLabel(
            task.completedBy,
            task.completedByName,
            taskPermissions?.userNames || {}
        );
    }, [taskPermissions]);

    // Check if current user has permission for a task
    const hasPermission = useCallback((taskKey) => {
        // Dev mode always has full access
        const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isDev) return true;
        
        if (!taskPermissions) return true; // Allow while loading
        
        return taskPermissions.taskPermissions?.[taskKey] ?? true;
    }, [taskPermissions, currentUser]);

    // Check if a task has uncompleted prerequisites
    const getUncompletedPrereqs = useCallback((taskKey) => {
        const taskDef = activeTaskDefinitions.find(t => t.key === taskKey);
        if (!taskDef) return [];
        
        return (taskDef.recommendedBefore || []).filter(prereqKey => {
            const prereqTask = tasks[prereqKey];
            return !prereqTask?.completed;
        });
    }, [tasks, activeTaskDefinitions]);

    // Check if task has hard requirements that block completion
    const hasHardBlockers = useCallback((taskKey) => {
        const taskDef = activeTaskDefinitions.find(t => t.key === taskKey);
        if (!taskDef?.requiresAllPrevious) return false;
        
        // Check if ALL previous tasks are complete
        const uncompletedPrereqs = getUncompletedPrereqs(taskKey);
        return uncompletedPrereqs.length > 0;
    }, [getUncompletedPrereqs, activeTaskDefinitions]);

    const openWeightMissingModal = (issues = []) => {
        setWeightValidationIssues(Array.isArray(issues) ? issues : []);
        setShowWeightMissingModal(true);
    };

    const verifyPoItemsHaveWeights = async () => {
        const response = await axios.post(
            `${API_URL}/api/purchase-orders/${po.id}/weight-check`,
            {
                storeKey: po.storeKey,
                items: po.items,
                line_items: po.line_items
            },
            { withCredentials: true }
        );
        return {
            ok: response.data?.ok === true,
            issues: response.data?.issues || []
        };
    };

    // Handle task toggle with optional warning
    const handleTaskToggle = async (taskKey, newValue) => {
        const taskDef = activeTaskDefinitions.find(t => t.key === taskKey);

        // Reverting a completed step is deliberate: confirm before reopening it so a
        // stray click on the small tick badge can't silently undo progress (and fire
        // a stage-change email). Completing stays friction-free.
        if (newValue === false) {
            const revertLabel = taskDef?.shortLabel || taskDef?.label || taskKey;
            if (typeof window !== 'undefined' && window.confirm
                && !window.confirm(`Reopen "${revertLabel}"? This marks the step as not done.`)) {
                return;
            }
        }

        // Check permission from settings (replaces hardcoded check)
        if (newValue === true && !hasPermission(taskKey)) {
            const taskLabel = taskDef?.shortLabel || taskDef?.label || taskKey;
            setError(`You don't have permission to complete "${taskLabel}"`);
            return;
        }

        if (newValue === true && taskKey === 'verifyProductData') {
            setIsLoading(prev => ({ ...prev, [taskKey]: true }));
            setError(null);
            try {
                const weightCheck = await verifyPoItemsHaveWeights();
                if (!weightCheck.ok) {
                    openWeightMissingModal(weightCheck.issues);
                    return;
                }
            } catch (err) {
                console.error('PO weight validation error:', err);
                setError(err.response?.data?.error || 'Failed to verify product weights');
                return;
            } finally {
                setIsLoading(prev => ({ ...prev, [taskKey]: false }));
            }
        }
        
        // If completing a task, check for warnings and special handling
        if (newValue === true) {
            // Check for hard blockers first (like putAway requiring all others)
            if (taskDef?.requiresAllPrevious) {
                const uncompletedPrereqs = getUncompletedPrereqs(taskKey);
                if (uncompletedPrereqs.length > 0) {
                    const prereqLabels = uncompletedPrereqs
                        .map(key => activeTaskDefinitions.find(t => t.key === key)?.label || key)
                        .join(', ');
                    setError(`Cannot complete "${taskDef.label}" until these are done: ${prereqLabels}`);
                    return;
                }
            }
            
            // Special handling for notifyArrival - opens receipt modal
            if (taskDef?.opensReceiptModal && onOpenReceiptModal) {
                onOpenReceiptModal(po);
                return; // Receipt modal will handle the task completion
            }
            
            const uncompletedPrereqs = getUncompletedPrereqs(taskKey);
            
            if (uncompletedPrereqs.length > 0) {
                if (taskDef?.actionType) {
                    const prereqLabels = uncompletedPrereqs
                        .map(key => activeTaskDefinitions.find(t => t.key === key)?.label || key)
                        .join(', ');
                    setError(`Cannot run "${taskDef.label}" until these are done: ${prereqLabels}`);
                    return;
                }

                if (canSkipWorkflowWarnings()) {
                    await executeTaskToggle(taskKey, newValue);
                    return;
                }

                // Show warning modal for soft prerequisites
                setPendingTask({ taskKey, newValue, uncompletedPrereqs });
                setShowWarningModal(true);
                return;
            }

            if (taskDef?.actionType && onTaskAction) {
                setIsLoading(prev => ({ ...prev, [taskKey]: true }));
                try {
                    const handled = await onTaskAction(po, taskDef);
                    if (handled) return;
                } catch (err) {
                    console.error('Task action error:', err);
                    setError(err.response?.data?.error || err.message || 'Failed to run task action');
                    return;
                } finally {
                    setIsLoading(prev => ({ ...prev, [taskKey]: false }));
                }
            }
        }
        
        // No warning needed, proceed directly
        await executeTaskToggle(taskKey, newValue);
    };

    // Execute the actual task toggle API call
    const executeTaskToggle = async (taskKey, completed, override = null, extraBody = null) => {
        setIsLoading(prev => ({ ...prev, [taskKey]: true }));
        setError(null);

        try {
            const response = await axios.post(
                `${API_URL}/api/purchase-orders/${po.id}/task`,
                {
                    taskKey,
                    completed,
                    storeKey: po.storeKey,
                    poItems: po.items || po.line_items || undefined,
                    override: override ? {
                        reason: override.reason,
                        note: overrideReason || null
                    } : null,
                    ...(extraBody || {})
                },
                { withCredentials: true }
            );

            if (response.data.success && onTaskUpdate) {
                setDispatchCheck(null);
                onTaskUpdate(po, response.data.tasks, response.data.status);
            }
        } catch (err) {
            console.error('Task toggle error:', err);
            const responseData = err.response?.data || {};
            const apiError = responseData.error;
            if (responseData.code === PO_WEIGHT_MISSING_CODE || apiError?.startsWith?.('Weights missing')) {
                openWeightMissingModal(responseData.issues || []);
            } else if (responseData.code === 'PO_PREORDER_DISPATCH_BLOCKED' || responseData.code === 'PO_PREORDER_DISPATCH_CONFIRM') {
                setDispatchCheck({
                    mode: responseData.code === 'PO_PREORDER_DISPATCH_BLOCKED' ? 'blocked' : 'confirm',
                    ...(responseData.check || {})
                });
            } else {
                setError(apiError || 'Failed to update task');
            }
        } finally {
            setIsLoading(prev => ({ ...prev, [taskKey]: false }));
            setShowWarningModal(false);
            setPendingTask(null);
            setOverrideReason('');
        }
    };

    // Red override on the weights-missing modal: ticks step 3 anyway. Needed for
    // POs carrying placeholder lines (e.g. SKU 9999) that can never pass the check.
    const handleWeightOverride = () => {
        setShowWeightMissingModal(false);
        setWeightValidationIssues([]);
        executeTaskToggle('verifyProductData', true,
            { reason: 'Weight check overridden — blocked lines ignored (e.g. placeholder SKU)' },
            { weightCheckOverride: true });
    };

    // Red override on the blocked dispatch modal: ticks step 6 anyway. Needed when
    // the PO was received by mistake so the listed orders genuinely cannot dispatch.
    const handleDispatchOverride = () => {
        setDispatchCheck(null);
        executeTaskToggle('checkPreorders', true,
            { reason: 'Preorder dispatch check overridden — listed orders cannot dispatch yet' },
            { preorderCheckAcknowledged: true, preorderCheckOverride: true });
    };

    const renderOverrideButton = (onClick, label = 'Override — tick anyway') => (
        <button
            onClick={onClick}
            style={{
                border: '1px solid #b91c1c',
                borderRadius: '8px',
                background: '#dc2626',
                color: 'white',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                padding: '8px 14px',
                whiteSpace: 'nowrap'
            }}
        >
            {label}
        </button>
    );

    // Handle proceeding past warning
    const handleProceedAnyway = () => {
        if (pendingTask) {
            const prereqLabels = pendingTask.uncompletedPrereqs
                .map(key => activeTaskDefinitions.find(t => t.key === key)?.shortLabel || key)
                .join(', ');
            
            executeTaskToggle(pendingTask.taskKey, pendingTask.newValue, {
                reason: `Proceeded without completing: ${prereqLabels}`
            });
        }
    };

    // One order row in the dispatch cross-check modal.
    const renderDispatchOrderRow = (row, showAwaiting = false) => (
        <div
            key={row.orderNumber}
            style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: `1px solid ${darkMode ? '#4b5563' : '#e0e0e0'}`,
                background: darkMode ? '#2a2a2a' : '#fafafa'
            }}
        >
            <InlineStack gap="200" align="start" blockAlign="center">
                {row.adminUrl ? (
                    <a
                        href={row.adminUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: 600, fontSize: '13px' }}
                    >
                        {row.orderNumber} ↗
                    </a>
                ) : (
                    <Text variant="bodySm" fontWeight="semibold">{row.orderNumber}</Text>
                )}
                {row.customerName && (
                    <Text variant="bodySm" tone="subdued">{row.customerName}</Text>
                )}
                {row.reason === 'pos_split_needed' ? (
                    <Badge tone="critical" size="small">fulfilled at POS · split needed</Badge>
                ) : (
                    <Badge tone={String(row.fulfillmentStatus || '').toUpperCase() === 'FULFILLED' ? 'success' : 'attention'} size="small">
                        {row.fulfillmentStatus
                            ? String(row.fulfillmentStatus).replace(/_/g, ' ').toLowerCase()
                            : 'status unknown'}
                    </Badge>
                )}
            </InlineStack>
            <div style={{ fontSize: '12px', marginTop: '4px', color: darkMode ? '#9ca3af' : '#666' }}>
                On this PO: {(row.matchedItems || []).map(item => `${item.qty}x ${item.sku || item.title}`).join(', ')}
            </div>
            {(row.notCoveredItems || []).length > 0 && (
                <div style={{ fontSize: '12px', marginTop: '2px', color: darkMode ? '#fbbf24' : '#92400e' }}>
                    Not covered by this PO (older orders got the stock): {(row.notCoveredItems || []).map(item => `${item.sku || item.title} ${item.shortQty} short`).join(', ')}
                </div>
            )}
            {showAwaiting && (row.awaitingItems || []).length > 0 && (
                <div style={{ fontSize: '12px', marginTop: '2px', color: darkMode ? '#fbbf24' : '#92400e' }}>
                    Still waiting on: {(row.awaitingItems || []).map(item => `${item.qty}x ${item.sku || item.title}`).join(', ')}
                </div>
            )}
            {(row.arrivedItems || []).length > 0 && (
                <div
                    style={{ fontSize: '12px', marginTop: '2px', color: darkMode ? '#86efac' : '#166534' }}
                    title="These lines' stock already arrived on another PO, so the order is not waiting on them"
                >
                    Already arrived: {(row.arrivedItems || []).map(item => `${item.qty}x ${item.sku || item.title}${item.poRef ? ` (${item.poRef})` : ''}`).join(', ')}
                </div>
            )}
        </div>
    );

    // Check if all tasks are complete
    const allTasksComplete = activeTaskDefinitions.every(
        taskDef => tasks[taskDef.key]?.completed === true
    );

    // Calculate progress
    const completedCount = activeTaskDefinitions.filter(
        taskDef => tasks[taskDef.key]?.completed === true
    ).length;

    // Render individual task row
    const renderTaskRow = (taskDef) => {
        const task = tasks[taskDef.key] || {};
        const isComplete = task.completed === true;
        const taskIndex = activeTaskDefinitions.indexOf(taskDef);
        
        // Only show override warning if previous tasks are STILL incomplete
        // This way, if tasks are unticked and re-done in order, the warning disappears
        const hasOverrideFlag = task.override?.isOverride === true;
        const previousTasksIncomplete = activeTaskDefinitions.slice(0, taskIndex).some(
            prevTaskDef => tasks[prevTaskDef.key]?.completed !== true
        );
        const hasOverride = hasOverrideFlag && previousTasksIncomplete;
        
        const loading = isLoading[taskDef.key];
        const isBlocked = hasHardBlockers(taskDef.key);
        const isActionTask = Boolean(taskDef.actionType && taskDef.actionLabel && onTaskAction);
        const handleManualComplete = async (event) => {
            event?.stopPropagation?.();
            if (!hasPermission(taskDef.key)) {
                const taskLabel = taskDef.shortLabel || taskDef.label || taskDef.key;
                setError(`You don't have permission to complete "${taskLabel}"`);
                return;
            }

            const uncompletedPrereqs = getUncompletedPrereqs(taskDef.key);
            if (uncompletedPrereqs.length > 0) {
                const prereqLabels = uncompletedPrereqs
                    .map(key => activeTaskDefinitions.find(t => t.key === key)?.label || key)
                    .join(', ');
                setError(`Cannot complete "${taskDef.label}" until these are done: ${prereqLabels}`);
                return;
            }

            await executeTaskToggle(taskDef.key, true);
        };

        if (compact) {
            // Compact mode: vertical stepper with timeline
            const completedBy = getCompletedByLabel(task);
            const isLastTask = taskIndex === activeTaskDefinitions.length - 1;
            
            // Determine if this is the "next" task (first incomplete, non-blocked task)
            const isNextTask = !isComplete && !isBlocked && 
                activeTaskDefinitions.findIndex(t => {
                    const tTask = tasks[t.key] || {};
                    return !tTask.completed && !hasHardBlockers(t.key);
                }) === taskIndex;
            
            // Get the label without the number prefix
            const labelText = (taskDef.shortLabel || taskDef.label || taskDef.key).replace(/^\d+\.\s*/, '');
            
            return (
                <div key={taskDef.key} style={{ display: 'flex', position: 'relative' }}>
                    {/* Timeline column with circle and connecting line.
                        22px circle / 26px rail (2026-08-29 density pass): the size is
                        CONSTANT across all four states — only colour changes — so rows
                        never shift when a step completes. */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '26px',
                        flexShrink: 0,
                        position: 'relative'
                    }}>
                        {/* Circle indicator */}
                        <div
                            /* --active carries the dark-on-bright label; App.css re-asserts it
                               because App.js's `.dark-mode div {color:#e5e5e5 !important}`
                               catch-all outranks an inline colour. */
                            className={`po-step-circle${isComplete || isNextTask ? ' po-step-circle--active' : ''}`}
                            style={{
                                width: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                fontWeight: 700,
                                flexShrink: 0,
                                background: isComplete
                                    ? '#22c55e'
                                    : isNextTask
                                        ? '#f59e0b'
                                        : darkMode ? '#4b5563' : '#e8e8e8',
                                // White on #22c55e / #f59e0b is under 2:1 in BOTH themes; near-black is ~10:1.
                                color: isComplete || isNextTask ? '#1a1a1a' : (darkMode ? '#9ca3af' : '#888'),
                                border: isNextTask ? `2px solid ${darkMode ? '#fcd34d' : '#fcd34d'}` : 'none',
                                boxSizing: 'border-box',
                                zIndex: 1,
                                position: 'relative'
                            }}
                        >
                            {isBlocked ? '🔒' : (taskIndex + 1)}
                            {/* Small tick badge for completed - clickable to untick */}
                            {isComplete && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleTaskToggle(taskDef.key, false);
                                    }}
                                    disabled={loading}
                                    title="Click to mark as incomplete"
                                    style={{
                                        position: 'absolute',
                                        bottom: '-3px',
                                        right: '-3px',
                                        width: '13px',
                                        height: '13px',
                                        borderRadius: '50%',
                                        // #16a34a put the white tick at 3.3:1; #15803d lifts it to ~5:1.
                                        background: darkMode ? '#15803d' : '#2e7d32',
                                        border: `2px solid ${darkMode ? '#1f2937' : 'white'}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '9px',
                                        color: 'white',
                                        cursor: loading ? 'wait' : 'pointer',
                                        padding: 0,
                                        transition: 'transform 0.1s ease'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                >✓</button>
                            )}
                        </div>
                        {/* Connecting line (not on last item) */}
                        {!isLastTask && (
                            <div style={{
                                width: '2px',
                                flex: 1,
                                minHeight: '4px',
                                background: isComplete ? (darkMode ? '#22c55e' : '#4caf50') : (darkMode ? '#4b5563' : '#e0e0e0')
                            }} />
                        )}
                    </div>

                    {/* Content area - row with label and checkbox */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        marginLeft: '8px',
                        marginBottom: isLastTask ? '0' : '3px'
                    }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                // 4px block padding puts a plain row at ~26-30px tall
                                // (2026-08-29 density pass); the 22px action column is
                                // the touch-height floor.
                                padding: '4px 10px',
                                borderRadius: '8px',
                                border: isNextTask ? `1.5px solid ${darkMode ? '#b45309' : '#fbbf24'}` : '1.5px solid transparent',
                                boxSizing: 'border-box',
                                background: isNextTask 
                                    ? (darkMode ? '#422006' : '#fef9e7')
                                    : isComplete 
                                        ? (darkMode ? '#14532d' : '#e8f5e9')
                                        : (darkMode ? 'rgba(55, 65, 81, 0.5)' : 'transparent'),
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {/* Step label with inline warning */}
                            <div style={{ 
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '13px',
                                fontWeight: isNextTask ? 600 : 500,
                                color: isNextTask 
                                    ? (darkMode ? '#fcd34d' : '#92400e')
                                    : isComplete 
                                        ? (darkMode ? '#86efac' : '#2e7d32')
                                        : (darkMode ? '#9ca3af' : '#666'),
                                opacity: (isBlocked && !isComplete) ? 0.5 : 1
                            }}>
                                <span>{taskIndex + 1}. {labelText}</span>
                                {hasOverride && (
                                    <span 
                                        title={task.override?.note ? `Reason: ${task.override.note}` : 'Completed out of order'}
                                        style={{ cursor: 'help' }}
                                    >
                                        ⚠️
                                    </span>
                                )}
                            </div>
                            
                            {/* Right side: checkbox OR username for alignment */}
                            <div style={{
                                minWidth: isActionTask && !isComplete ? (taskDef.allowManualComplete ? '136px' : '112px') : '22px',
                                height: '22px',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: '4px'
                            }}>
                                {/* Show username for completed tasks */}
                                {isComplete && completedBy && (
                                    <span style={{ 
                                        fontSize: '11px', 
                                        color: darkMode ? '#9ca3af' : '#888',
                                        fontWeight: 400,
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {completedBy}
                                    </span>
                                )}
                                {/* Only show checkbox for active step, lock for blocked, nothing for completed */}
                                {isBlocked && !isComplete ? (
                                    <span style={{ 
                                        fontSize: '13px',
                                        opacity: 0.4
                                    }}>
                                        🔒
                                    </span>
                                ) : isActionTask && !isComplete ? (
                                    <>
                                        <button
                                            onClick={() => handleTaskToggle(taskDef.key, true)}
                                            disabled={loading || !isNextTask}
                                            style={{
                                                border: '1px solid #d97706',
                                                borderRadius: '4px',
                                                background: isNextTask ? '#f97316' : (darkMode ? '#374151' : '#f3f4f6'),
                                                color: isNextTask ? 'white' : (darkMode ? '#9ca3af' : '#9ca3af'),
                                                cursor: loading ? 'wait' : (isNextTask ? 'pointer' : 'not-allowed'),
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                // Same 22px control height as the tick boxes so every
                                                // row's right edge sits on one line (2026-08-29).
                                                padding: '2px 8px',
                                                minWidth: taskDef.allowManualComplete ? '96px' : '104px',
                                                height: '22px',
                                                whiteSpace: 'nowrap',
                                                transition: 'all 0.15s ease'
                                            }}
                                            title={isNextTask ? taskDef.actionLabel : 'Complete previous tasks first'}
                                        >
                                            {loading ? 'Working...' : taskDef.actionLabel}
                                        </button>
                                        {taskDef.allowManualComplete && (
                                            <button
                                                onClick={handleManualComplete}
                                                disabled={loading || !isNextTask}
                                                aria-label={`Mark ${taskDef.label} complete`}
                                                title="Mark complete after printing labels from the grid"
                                                style={{
                                                    width: '22px',
                                                    height: '22px',
                                                    borderRadius: '4px',
                                                    border: `2px solid ${darkMode ? '#6b7280' : '#ccc'}`,
                                                    background: darkMode ? '#374151' : 'white',
                                                    cursor: loading ? 'wait' : (isNextTask ? 'pointer' : 'not-allowed'),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: 0,
                                                    transition: 'all 0.15s ease'
                                                }}
                                            />
                                        )}
                                    </>
                                ) : isNextTask ? (
                                    <button
                                        onClick={() => handleTaskToggle(taskDef.key, true)}
                                        disabled={loading}
                                        style={{
                                            width: '22px',
                                            height: '22px',
                                            borderRadius: '4px',
                                            border: '2px solid #d97706',
                                            background: darkMode ? '#374151' : 'white',
                                            cursor: loading ? 'wait' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0,
                                            transition: 'all 0.15s ease'
                                        }}
                                        title="Mark as complete"
                                    />
                                ) : !isComplete && !isBlocked ? (
                                    <button
                                        onClick={() => handleTaskToggle(taskDef.key, true)}
                                        disabled={loading}
                                        style={{
                                            width: '22px',
                                            height: '22px',
                                            borderRadius: '4px',
                                            border: `2px solid ${darkMode ? '#6b7280' : '#ccc'}`,
                                            background: darkMode ? '#374151' : 'white',
                                            cursor: loading ? 'wait' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0,
                                            opacity: 0.6,
                                            transition: 'all 0.15s ease'
                                        }}
                                        title="Mark as complete"
                                    />
                                ) : null /* No checkbox for completed steps */}
                            </div>
                        </div>
                        
                        {/* Microcopy helper text for current step only — hugs the
                            label above it rather than opening a band of its own. */}
                        {isNextTask && (
                            <div style={{
                                fontSize: '12px',
                                lineHeight: 1.35,
                                color: darkMode ? '#9ca3af' : '#666',
                                marginTop: '2px',
                                marginLeft: '10px',
                                fontStyle: 'italic'
                            }}>
                                {taskDef.description}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // Full mode: detailed row
        return (
            <div 
                key={taskDef.key}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: darkMode 
                        ? (isComplete ? '#1b3d1b' : '#2a2a2a') 
                        : (isComplete ? '#f1f8e9' : '#fafafa'),
                    border: `1px solid ${darkMode ? (isComplete ? '#22c55e' : '#4b5563') : (isComplete ? '#81c784' : '#e0e0e0')}`,
                    marginBottom: '6px',
                    transition: 'all 0.2s ease'
                }}
            >
                <InlineStack gap="300" align="center">
                    {isActionTask && !isComplete ? (
                        <InlineStack gap="100" align="center">
                            <button
                                onClick={() => handleTaskToggle(taskDef.key, true)}
                                disabled={loading}
                                style={{
                                    border: '1px solid #d97706',
                                    borderRadius: '4px',
                                    background: '#f97316',
                                    color: 'white',
                                    cursor: loading ? 'wait' : 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    padding: '4px 10px',
                                    minWidth: taskDef.allowManualComplete ? '96px' : '112px',
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                {loading ? 'Working...' : taskDef.actionLabel}
                            </button>
                            {taskDef.allowManualComplete && (
                                <button
                                    onClick={handleManualComplete}
                                    disabled={loading}
                                    aria-label={`Mark ${taskDef.label} complete`}
                                    title="Mark complete after printing labels from the grid"
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '4px',
                                        border: `2px solid ${darkMode ? '#6b7280' : '#bdbdbd'}`,
                                        background: 'transparent',
                                        cursor: loading ? 'wait' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontSize: '14px',
                                        padding: 0,
                                        transition: 'all 0.15s ease'
                                    }}
                                />
                            )}
                        </InlineStack>
                    ) : (
                        <button
                            onClick={() => handleTaskToggle(taskDef.key, !isComplete)}
                            disabled={loading}
                            style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px',
                                border: `2px solid ${isComplete ? (darkMode ? '#22c55e' : '#4caf50') : (darkMode ? '#6b7280' : '#bdbdbd')}`,
                                background: isComplete ? (darkMode ? '#22c55e' : '#4caf50') : 'transparent',
                                cursor: loading ? 'wait' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '14px',
                                padding: 0,
                                transition: 'all 0.15s ease'
                            }}
                        >
                            {isComplete && '✓'}
                        </button>
                    )}
                    
                    <BlockStack gap="100">
                        <Text variant="bodyMd" fontWeight={isComplete ? 'medium' : 'regular'}>
                            {taskDef.label}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            {taskDef.description}
                        </Text>
                    </BlockStack>
                </InlineStack>

                <InlineStack gap="200" align="center">
                    {hasOverride && (
                        <Badge tone="warning" size="small">
                            Out of order
                        </Badge>
                    )}
                    
                    {isComplete && task.completedBy && (
                        <Text variant="bodySm" tone="subdued">
                            {getCompletedByLabel(task)} • {new Date(task.completedAt).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}
                        </Text>
                    )}
                    
                    {loading && (
                        <span style={{ fontSize: '12px' }}>Saving...</span>
                    )}
                </InlineStack>
            </div>
        );
    };

    return (
        <div>
            {error && (
                <Banner tone="critical" onDismiss={() => setError(null)}>
                    {error}
                </Banner>
            )}

            {compact ? (
                // Compact: vertical stepper with timeline
                <div style={{ width: '100%' }}>
                    {/* Heading kept, but tight — it must not consume a full row
                        of its own (2026-08-29 density pass). */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        marginBottom: '6px'
                    }}>
                        <span style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: darkMode ? '#e5e7eb' : '#333'
                        }}>
                            {workflowTitle}
                        </span>
                        <span style={{
                            fontSize: '12px',
                            color: allTasksComplete ? (darkMode ? '#86efac' : '#2e7d32') : (darkMode ? '#9ca3af' : '#666'),
                            marginLeft: '8px',
                            fontWeight: 500
                        }}>
                            ({completedCount}/{activeTaskDefinitions.length})
                        </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {activeTaskDefinitions.map(renderTaskRow)}
                    </div>
                </div>
            ) : (
                // Full: vertical list with details
                <BlockStack gap="200">
                    <InlineStack align="space-between">
                        <Text variant="headingSm">{fullModeTitle}</Text>
                        <Badge tone={allTasksComplete ? 'success' : 'info'}>
                            {completedCount}/{activeTaskDefinitions.length} Complete
                        </Badge>
                    </InlineStack>
                    
                    <div style={{ marginTop: '8px' }}>
                        {activeTaskDefinitions.map(renderTaskRow)}
                    </div>

                    {allTasksComplete && (
                        <Banner tone="success">
                            {completionMessage}
                        </Banner>
                    )}
                </BlockStack>
            )}

            <Modal
                open={showWeightMissingModal}
                onClose={() => {
                    setShowWeightMissingModal(false);
                    setWeightValidationIssues([]);
                }}
                title="Weights missing"
                primaryAction={{
                    content: 'OK',
                    onAction: () => {
                        setShowWeightMissingModal(false);
                        setWeightValidationIssues([]);
                    }
                }}
                footer={renderOverrideButton(handleWeightOverride)}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        <Text>
                            {weightValidationIssues.length > 0
                                ? 'These PO lines are blocking step 3. Load them in the product grid, confirm weight is saved, then try again.'
                                : 'Weights missing, please load products in grid and try again.'}
                        </Text>
                        {weightValidationIssues.length > 0 && (
                            <BlockStack gap="200">
                                {weightValidationIssues.map((issue, index) => (
                                    <Text key={`${issue?.sku || 'line'}-${issue?.reason || index}-${index}`} as="p">
                                        • {formatWeightIssueLine(issue)}
                                    </Text>
                                ))}
                            </BlockStack>
                        )}
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {/* Step 6 dispatch cross-check: preorder orders on this PO not yet dispatched */}
            <Modal
                open={!!dispatchCheck}
                onClose={() => setDispatchCheck(null)}
                title={dispatchCheck?.mode === 'blocked' ? 'Preorder orders not dispatched' : 'Some preorder orders are still waiting'}
                primaryAction={dispatchCheck?.mode === 'blocked'
                    ? { content: 'OK — I\'ll check picking', onAction: () => setDispatchCheck(null) }
                    : {
                        content: 'Tick anyway',
                        onAction: () => executeTaskToggle('checkPreorders', true, null, { preorderCheckAcknowledged: true })
                    }}
                secondaryActions={dispatchCheck?.mode === 'blocked' ? [] : [
                    { content: 'Cancel', onAction: () => setDispatchCheck(null) }
                ]}
                footer={dispatchCheck?.mode === 'blocked'
                    ? renderOverrideButton(handleDispatchOverride)
                    : undefined}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        {(dispatchCheck?.blockers || []).length > 0 && (
                            <BlockStack gap="200">
                                <Banner tone="critical">
                                    These orders hold preorder stock from this PO and have nothing else to wait for,
                                    but they are not dispatched in Shopify. The picking flow may have missed them —
                                    check the picking queue and dispatch them, then tick this step again.
                                </Banner>
                                {(dispatchCheck?.blockers || []).map(row => renderDispatchOrderRow(row))}
                            </BlockStack>
                        )}
                        {(dispatchCheck?.waiting || []).length > 0 && (
                            <BlockStack gap="200">
                                <Banner tone="warning">
                                    These orders also wait on other preorders that haven't arrived yet, so they
                                    won't dispatch now — there will be more orders to fulfill and check when that
                                    stock lands.
                                </Banner>
                                {(dispatchCheck?.waiting || []).map(row => renderDispatchOrderRow(row, true))}
                            </BlockStack>
                        )}
                        {(dispatchCheck?.unknown || []).length > 0 && (
                            <BlockStack gap="200">
                                <Banner tone="info">
                                    Shopify status couldn't fully prove these orders are dispatched. Check them manually,
                                    then tick anyway if the preorder stock from this PO has shipped.
                                </Banner>
                                {(dispatchCheck?.unknown || []).map(row => renderDispatchOrderRow(row))}
                            </BlockStack>
                        )}
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {/* Warning Modal for out-of-order completion */}
            <Modal
                open={showWarningModal}
                onClose={() => {
                    setShowWarningModal(false);
                    setPendingTask(null);
                    setOverrideReason('');
                }}
                title="⚠️ Completing Task Out of Order"
                primaryAction={{
                    content: 'Proceed Anyway',
                    onAction: handleProceedAnyway,
                    destructive: true
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: () => {
                            setShowWarningModal(false);
                            setPendingTask(null);
                            setOverrideReason('');
                        }
                    }
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        <Text>
                            You're about to complete <strong>
                                {pendingTask && activeTaskDefinitions.find(t => t.key === pendingTask.taskKey)?.label}
                            </strong> before finishing:
                        </Text>
                        
                        <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>
                            {pendingTask?.uncompletedPrereqs.map(key => {
                                const taskDef = activeTaskDefinitions.find(t => t.key === key);
                                return (
                                    <li key={key} style={{ marginBottom: '4px' }}>
                                        <Text tone="critical">{taskDef?.label}</Text>
                                    </li>
                                );
                            })}
                        </ul>

                        <Text tone="subdued">
                            This will be logged for audit purposes. You can optionally add a reason:
                        </Text>
                        
                        <TextField
                            label="Reason (optional)"
                            value={overrideReason}
                            onChange={setOverrideReason}
                            placeholder="e.g., Needed to complete this before other checks"
                            multiline={2}
                        />
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </div>
    );
};

export default POTaskChecklist;
